"use client";

import { useEffect, useMemo, useState } from "react";
import {
  readLocalFirstCache,
  writeLocalFirstCache,
} from "@/lib/local-first-cache";

type UsePersistentReactiveQueryValueOptions<T> = {
  storageKey: string;
  scopeKey: string;
  liveValue: T | undefined;
  enabled?: boolean;
};

type UsePersistentReactiveQueryValueResult<T> = {
  value: T | undefined;
  hasCachedValue: boolean;
  isUsingCachedValue: boolean;
  liveValue: T | undefined;
};

export const usePersistentReactiveQueryValue = <T>({
  storageKey,
  scopeKey,
  liveValue,
  enabled = true,
}: UsePersistentReactiveQueryValueOptions<T>): UsePersistentReactiveQueryValueResult<T> => {
  const [cachedValue, setCachedValue] = useState<T | undefined>(undefined);
  const [hasCachedValue, setHasCachedValue] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setCachedValue(undefined);
      setHasCachedValue(false);
      return;
    }

    const cached = readLocalFirstCache<T>(storageKey, scopeKey);
    if (cached.hasValue) {
      setCachedValue(cached.value);
      setHasCachedValue(true);
      return;
    }

    setCachedValue(undefined);
    setHasCachedValue(false);
  }, [enabled, scopeKey, storageKey]);

  useEffect(() => {
    if (!enabled || liveValue === undefined || liveValue === null) {
      return;
    }

    setCachedValue(liveValue);
    setHasCachedValue(true);
    writeLocalFirstCache(storageKey, scopeKey, liveValue);
  }, [enabled, liveValue, scopeKey, storageKey]);

  const value = useMemo(() => {
    if (liveValue === undefined && hasCachedValue) {
      return cachedValue;
    }
    return liveValue;
  }, [cachedValue, hasCachedValue, liveValue]);

  return {
    value,
    hasCachedValue,
    isUsingCachedValue: liveValue === undefined && hasCachedValue,
    liveValue,
  };
};
